import {
  Alert,
  AlertActions,
  AlertDescription,
  AlertTitle,
  Button,
  EditableText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@criteria-labs/react'
import { Endpoint } from '@criteria-labs/docs-kit'
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/16/solid'
import debounce from 'lodash.debounce'
import { useMemo, useState } from 'react'
import { DeleteEndpoint } from '../changes/endpoint'
import { UpdateOperationSummary } from '../changes/operation'
import { useDispatchChange } from '../useDispatchChange'
import { EndpointLine } from './EndpointLine'
import clsx from 'clsx'
import { useRouter } from '../router'

const allSortProperties = [
  ['path', 'method'],
  ['method', 'path'],
  ['summary', 'path', 'method'],
  ['resource', 'path', 'method']
] as const

export function EndpointList({
  endpoints,
  showResourceColumn
}: {
  endpoints: Endpoint[]
  showResourceColumn: boolean
}) {
  const { push } = useRouter()

  const dispatchChange = useDispatchChange()
  const debouncedDispatchChange = useMemo(() => debounce(dispatchChange), [dispatchChange])
  const [deletingEndpoint, setDeletingEndpoint] = useState<Endpoint | null>(null)

  const [sortProperties, setSortProperties] = useState<(typeof allSortProperties)[number]>(['path', 'method'])
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const sortedEndpoints = endpoints.sort((a, b) => {
    for (let i = 0; i < sortProperties.length; i++) {
      const sortProperty = sortProperties[i]
      const ascendingSort =
        (a[sortProperty] ?? '') < (b[sortProperty] ?? '')
          ? -1
          : (a[sortProperty] ?? '') > (b[sortProperty] ?? '')
          ? 1
          : 0
      if (ascendingSort !== 0) {
        return sortDirection === 'asc' ? ascendingSort : -1 * ascendingSort
      }
    }
    return 0
  })

  const SortIcon = sortDirection === 'asc' ? ChevronUpIcon : ChevronDownIcon

  return (
    <>
      <Table dense bleed className="[--gutter:0]">
        <TableHead>
          <TableRow>
            <TableHeader>
              <button
                className="group flex w-full items-center gap-1 dark:hover:text-white"
                onClick={() => {
                  if (sortProperties[0] !== 'path' && sortProperties[0] !== 'method') {
                    setSortProperties(['path', 'method'])
                    setSortDirection('asc')
                  } else if (sortDirection === 'asc') {
                    setSortDirection('desc')
                  } else {
                    setSortProperties(sortProperties[0] === 'path' ? ['method', 'path'] : ['path', 'method'])
                    setSortDirection('asc')
                  }
                }}
              >
                Endpoint{' '}
                <SortIcon
                  className={clsx(
                    'size-4',
                    sortProperties[0] !== 'path' && sortProperties[0] !== 'method' && 'invisible group-hover:visible'
                  )}
                />
              </button>
            </TableHeader>
            <TableHeader>
              <button
                className="group flex w-full items-center gap-1 dark:hover:text-white"
                onClick={() => {
                  if (sortProperties[0] !== 'summary') {
                    setSortProperties(['summary', 'path', 'method'])
                    setSortDirection('asc')
                  } else {
                    setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                  }
                }}
              >
                Summary{' '}
                <SortIcon
                  className={clsx('size-4', sortProperties[0] !== 'summary' && 'invisible group-hover:visible')}
                />
              </button>
            </TableHeader>
            {showResourceColumn && (
              <TableHeader>
                <button
                  className="group flex w-full items-center gap-1 dark:hover:text-white"
                  onClick={() => {
                    if (sortProperties[0] !== 'resource') {
                      setSortProperties(['resource', 'path', 'method'])
                      setSortDirection('asc')
                    } else {
                      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                    }
                  }}
                >
                  Resource{' '}
                  <SortIcon
                    className={clsx('size-4', sortProperties[0] !== 'resource' && 'invisible group-hover:visible')}
                  />
                </button>
              </TableHeader>
            )}
            <TableHeader className="relative w-0">
              <span className="sr-only">Actions</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedEndpoints.map((endpoint) => (
            <TableRow
              key={endpoint.slug}
              href="#"
              onClick={() => push(`/endpoints/${encodeURIComponent(endpoint.path)}/${endpoint.method}`)}
            >
              <TableCell>
                <EndpointLine endpoint={endpoint} />
              </TableCell>
              <TableCell>
                <EditableText
                  value={endpoint.operation.summary}
                  onChange={(value) => {
                    debouncedDispatchChange(new UpdateOperationSummary(endpoint.operationRef, value))
                  }}
                />
              </TableCell>
              {showResourceColumn && <TableCell>{endpoint.resource?.summary ?? null}</TableCell>}
              <TableCell>
                <Button plain aria-label="Delete endpoint" onClick={() => setDeletingEndpoint(endpoint)}>
                  <XMarkIcon />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Alert open={deletingEndpoint !== null} onClose={() => setDeletingEndpoint(null)}>
        <AlertTitle>Delete Endpoint?</AlertTitle>
        {deletingEndpoint && (
          <AlertDescription>
            The endpoint <EndpointLine endpoint={deletingEndpoint} /> will be deleted.
          </AlertDescription>
        )}
        <AlertActions>
          <Button plain onClick={() => setDeletingEndpoint(null)}>
            Cancel
          </Button>
          {deletingEndpoint && (
            <Button
              onClick={() => {
                dispatchChange(new DeleteEndpoint(deletingEndpoint.path, deletingEndpoint.method))
                setDeletingEndpoint(null)
              }}
            >
              Delete
            </Button>
          )}
        </AlertActions>
      </Alert>
    </>
  )
}
